import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import validator from 'validator';
import { useNavigate } from 'react-router-dom';
import { postRequest } from '../../helpers/apiHandler';
import CreatableSelect from 'react-select/creatable';
import { MultiSelectStyle } from '../../helpers/MultiSelectStyle';
import CustomAlert from '../../components/CustomAlert';
import config from '../../config/Constant';

const ContactOurExperts = ({ user, access_token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name] = useState(user?.name);
  const [phone_number] = useState(user?.phone_number);
  const [email] = useState(user?.email);
  const [role, setRole] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [roleError, setRoleError] = useState({ error: false, message: '' });
  const [companySizeError, setCompanySizeError] = useState({ error: false, message: '' });
  const [productsError, setProductsError] = useState({ error: false, message: '' });
  const [messageError, setMessageError] = useState({ error: false, message: '' });

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const productOptions = [
    { value: 'sms-campaign', label: 'SMS Campaign' },
    { value: 'sms-api', label: 'SMS API' },
    { value: 'email-api', label: 'Email API' },
    { value: 'other-channels', label: 'Other Channels' },
  ];

  const handleSubmitForm = evt => {
    evt.preventDefault();
    const form = evt.currentTarget;

    if (form.checkValidity() === false) {
      evt.stopPropagation();
    }

    if (validator.isEmpty(role)) {
      setRoleError({
        error: true,
        message: 'Please enter your company role',
      });
      return;
    }

    if (validator.isEmpty(companySize)) {
      setCompanySizeError({
        error: true,
        message: 'Please choose your company size',
      });
      return;
    }

    if (products.length < 1) {
      setProductsError({
        error: true,
        message: 'Please choose products you are interested in',
      });
      return;
    }

    if (validator.isEmpty(message)) {
      setMessageError({
        error: true,
        message: 'Please enter your message',
      });
      return;
    }

    const data = {
      your_role: role,
      company_size: companySize,
      products: products.map(product => product.value),
      description: message,
    };

    postRequest('/v2/contact-expert', data, access_token.token, (res) => {
      if (res.success) {
        setAlertState({
          message: 'Channel request submitted successfully. A confirmation email has been sent to your inbox.',
          show: true,
          variant: 'success'
        });
        setTimeout(() => {
          navigate('/dashboard');
        }, config.notify_time)
      } else {
        setAlertState({
          message: 'Failed when submitting form. Please try again later',
          show: true,
          variant: 'danger'
        });
      }
    });
  };

  const params = new URLSearchParams(window.location.search);
  const product = params.get("product");

  useEffect(() => {
    if (product === "email") {
      setProducts([{ value: 'email-api', label: 'Email API' }])
    }
  }, [product]);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <div className="ContactOurExpertContainer">
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <div className="inner-wrapper">
        <h3 className="form-title">Contact our experts</h3>
        <p className="form-description">
          Getting to know you and your needs is a priority for us. Please enter the following details, and we'll be in touch shortly.
        </p>
        <Form noValidate onSubmit={handleSubmitForm}>
          <Form.Group>
            <Form.Control
              type="hidden"
              defaultValue={name}
              disabled
            />
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="hidden"
              defaultValue={phone_number}
              disabled
            />
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="hidden"
              defaultValue={email}
              disabled
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Your Role</Form.Label>
            <Form.Control
              placeholder="Your Role"
              name="role"
              value={role}
              onChange={evt => setRole(evt.target.value)}
              isInvalid={roleError.error}
            />
            <Form.Control.Feedback type="invalid">{roleError.message}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Company Size</Form.Label>
            <Form.Control
              as="select"
              placeholder="Choose Company Size"
              name="companySize"
              value={companySize}
              onChange={evt => setCompanySize(evt.target.value)}
              isInvalid={companySizeError.error}
            >
              <option value="">Choose Company Size</option>
              <option value="50">Less than 50 </option>
              <option value="50-100">50 - 100 </option>
              <option value="100">More than 100</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{companySizeError.message}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="productSelection">
            <Form.Label>Which Products are you interested in?</Form.Label>
            <CreatableSelect
              options={productOptions}
              isMulti
              value={products}
              onChange={setProducts}
              styles={MultiSelectStyle}
              noOptionsMessage={() => null} // Disable creation
              isValidNewOption={() => false} // Disable creation
            />
            <Form.Control.Feedback type="invalid">
              {productsError.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>How can we help?</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Description"
              name="message"
              value={message}
              onChange={evt => setMessage(evt.target.value)}
              isInvalid={messageError.error}
            />
            <Form.Control.Feedback type="invalid">{messageError.message}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-0">
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              style={{ minWidth: '160px' }}
            >
              {loading && <Spinner animation="border" size="sm" className="mr-2" />}
              Submit
            </Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const { access_token, user } = state.auth;
  return {
    access_token,
    user,
  };
};

const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ContactOurExperts);
